import _sortBy from "lodash/sortBy";

/**
 * Utility functions for calculating shareholding percentages and detecting changes
 */

/**
 * Round a number to specified decimal places
 */
export const roundNumber = (num, decimals = 2) => {
  return (
    Math.round((num + Number.EPSILON) * Math.pow(10, decimals)) /
    Math.pow(10, decimals)
  );
};

/**
 * Calculate shareholding percentages for all shareholders (cross-table scenario)
 * @param {Array} currentAppointmentsShareholders - Shareholders still in Current Appointments table
 * @param {Array} changeListShareholders - Shareholders moved to Changes List table (allocated)
 * @param {Array} shareCapitalChanges - Array of share capital changes from transactions
 * @returns {Object} Object with shareholding percentages by shareholder ID
 */
export const calculateShareholdingPercentagesCrossTable = (
  currentAppointmentsShareholders = [],
  changeListShareholders = [],
  shareCapitalChanges = [],
  votesPerShareByClass = {}
) => {
  // Step 1: Calculate total shares from BOTH tables (complete picture)
  let totalCurrentShares = 0;
  currentAppointmentsShareholders.forEach((shareholder) => {
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        totalCurrentShares += Number(share.allottedShares || 0);
      });
    }
  });
  changeListShareholders.forEach((shareholder) => {
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        totalCurrentShares += Number(share.allottedShares || 0);
      });
    }
  });

  // Step 2: Calculate shares on offer and allocations
  let totalSharesOnOffer = 0;
  const allocationsByShareholderId = {};
  const allocationsByShareholderIdByClass = {};
  const transferReductionByShareholderId = {};
  const transferReductionByShareholderIdByClass = {};

  shareCapitalChanges.forEach((change) => {
    const isTransferChange =
      Array.isArray(change.allocations) &&
      change.allocations.some(
        (a) => String(a?.type).toLowerCase() === "transfer"
      );

    const sharesOnOffer = Number(change.sharesOnOffer || 0);
    // Transfers do not issue new shares, keep denominator fixed
    if (!isTransferChange) {
      totalSharesOnOffer += sharesOnOffer;
    }

    if (Array.isArray(change.allocations)) {
      change.allocations.forEach((allocation) => {
        const shareholderId = allocation.officialId;
        const allocated = Number(allocation.allocatedShares || 0);
        const cls = String(change?.classOfShares || "");

        // Positive allocations increase transferees
        allocationsByShareholderId[shareholderId] =
          (allocationsByShareholderId[shareholderId] || 0) + allocated;
        if (cls) {
          const map = allocationsByShareholderIdByClass[shareholderId] || {};
          map[cls] = (map[cls] || 0) + allocated;
          allocationsByShareholderIdByClass[shareholderId] = map;
        }

        // Transferor reductions: use meta.amount where provided
        if (
          String(allocation?.type).toLowerCase() === "transfer" &&
          allocation?.meta?.note === "transferor" &&
          allocation?.meta?.amount
        ) {
          const reduction = Number(allocation.meta.amount || 0);
          transferReductionByShareholderId[shareholderId] =
            (transferReductionByShareholderId[shareholderId] || 0) + reduction;
          if (cls) {
            const map =
              transferReductionByShareholderIdByClass[shareholderId] || {};
            map[cls] = (map[cls] || 0) + reduction;
            transferReductionByShareholderIdByClass[shareholderId] = map;
          }
        }
      });
    }
  });

  // Step 3: Calculate total shares after allocation (declare ONCE)
  const totalSharesAfterAllocation = totalCurrentShares + totalSharesOnOffer;

  // Step 4: Calculate percentages for ALL shareholders
  const shareholderPercentages = {};

  let totalCurrentVotes = 0;
  const addVotesForShares = (sharesAllotted) => {
    if (!Array.isArray(sharesAllotted)) return;
    sharesAllotted.forEach((share) => {
      const vps = Number(votesPerShareByClass[share.classOfShares] || 0);
      if (vps > 0) {
        totalCurrentVotes += Number(share.allottedShares || 0) * vps;
      }
    });
  };
  currentAppointmentsShareholders.forEach((s) =>
    addVotesForShares(s.sharesAllotted)
  );
  changeListShareholders.forEach((s) => addVotesForShares(s.sharesAllotted));
  let totalVotesOnOffer = 0;
  shareCapitalChanges.forEach((change) => {
    const isTransferChange =
      Array.isArray(change.allocations) &&
      change.allocations.some(
        (a) => String(a?.type).toLowerCase() === "transfer"
      );
    if (!isTransferChange) {
      const vps = Number(votesPerShareByClass[change.classOfShares] || 0);
      if (vps > 0) {
        totalVotesOnOffer += Number(change.sharesOnOffer || 0) * vps;
      }
    }
  });
  const totalVotesAfterAllocation = totalCurrentVotes + totalVotesOnOffer;

  // Current Appointments shareholders
  currentAppointmentsShareholders.forEach((shareholder) => {
    const shareholderId = shareholder.id || shareholder._id;

    let currentShares = 0;
    const currentByClass = {};
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        const amt = Number(share.allottedShares || 0);
        currentShares += amt;
        const cls = String(share.classOfShares || "");
        if (cls) currentByClass[cls] = (currentByClass[cls] || 0) + amt;
      });
    }

    // NEW: apply reductions for transferors
    const reductionsMap =
      transferReductionByShareholderIdByClass[shareholderId] || {};
    let reducedTotal = 0;
    const reducedByClass = {};
    Object.keys(currentByClass).forEach((cls) => {
      const cur = currentByClass[cls] || 0;
      const red = reductionsMap[cls] || 0;
      const val = Math.max(0, cur - red);
      reducedByClass[cls] = val;
      reducedTotal += val;
    });
    const reducedShares =
      reducedTotal ||
      Math.max(
        0,
        currentShares - (transferReductionByShareholderId[shareholderId] || 0)
      );

    const newlyAllocatedShares = 0;
    const totalSharesForShareholder = reducedShares + newlyAllocatedShares;

    let votingTotal = 0;
    Object.keys(reducedByClass).forEach((cls) => {
      const vps = Number(votesPerShareByClass[cls] || 0);
      if (vps > 0) votingTotal += reducedByClass[cls] * vps;
    });

    const economicPercentage =
      totalSharesAfterAllocation > 0
        ? roundNumber(
            (totalSharesForShareholder / totalSharesAfterAllocation) * 100,
            2
          )
        : 0;
    const votingPercentage =
      totalVotesAfterAllocation > 0
        ? roundNumber((votingTotal / totalVotesAfterAllocation) * 100, 2)
        : 0;

    shareholderPercentages[shareholderId] = {
      name: shareholder.name,
      totalPercentage: economicPercentage,
      economicPercentage,
      votingPercentage,
      currentShares,
      newlyAllocatedShares,
      totalSharesForShareholder,
      totalSharesAfterAllocation,
      totalVotesForShareholder: votingTotal,
      totalVotesAfterAllocation,
      tableLocation: "current",
    };
  });

  // Changes List shareholders
  changeListShareholders.forEach((shareholder) => {
    const shareholderId = shareholder.id || shareholder._id;

    let currentShares = 0;
    const currentByClass = {};
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        const amt = Number(share.allottedShares || 0);
        currentShares += amt;
        const cls = String(share.classOfShares || "");
        if (cls) currentByClass[cls] = (currentByClass[cls] || 0) + amt;
      });
    }

    // NEW: apply reductions for transferors
    const reductionsMap =
      transferReductionByShareholderIdByClass[shareholderId] || {};
    let reducedTotal = 0;
    const reducedByClass = {};
    Object.keys(currentByClass).forEach((cls) => {
      const cur = currentByClass[cls] || 0;
      const red = reductionsMap[cls] || 0;
      const val = Math.max(0, cur - red);
      reducedByClass[cls] = val;
      reducedTotal += val;
    });
    const reducedShares =
      reducedTotal ||
      Math.max(
        0,
        currentShares - (transferReductionByShareholderId[shareholderId] || 0)
      );

    const newlyAllocatedShares = allocationsByShareholderId[shareholderId] || 0;
    const allocByClass = allocationsByShareholderIdByClass[shareholderId] || {};
    const totalSharesForShareholder = reducedShares + newlyAllocatedShares;

    let votingTotal = 0;
    const classes = new Set([
      ...Object.keys(reducedByClass),
      ...Object.keys(allocByClass),
    ]);
    classes.forEach((cls) => {
      const vps = Number(votesPerShareByClass[cls] || 0);
      if (vps > 0) {
        const base = reducedByClass[cls] || 0;
        const add = allocByClass[cls] || 0;
        votingTotal += (base + add) * vps;
      }
    });

    const economicPercentage =
      totalSharesAfterAllocation > 0
        ? roundNumber(
            (totalSharesForShareholder / totalSharesAfterAllocation) * 100,
            2
          )
        : 0;
    const votingPercentage =
      totalVotesAfterAllocation > 0
        ? roundNumber((votingTotal / totalVotesAfterAllocation) * 100, 2)
        : 0;

    shareholderPercentages[shareholderId] = {
      name: shareholder.name,
      totalPercentage: economicPercentage,
      economicPercentage,
      votingPercentage,
      currentShares,
      newlyAllocatedShares,
      totalSharesForShareholder,
      totalSharesAfterAllocation,
      totalVotesForShareholder: votingTotal,
      totalVotesAfterAllocation,
      tableLocation: "changes",
    };
  });

  return shareholderPercentages;
};

/**
 * Original function - kept for backward compatibility
 */
export const calculateShareholdingPercentages = (
  shareholders = [],
  nominalShareCapital = [],
  shareCapitalChanges = []
) => {
  // Debug the structure of shareCapitalChanges
  shareCapitalChanges.forEach((change, index) => {
    if (Array.isArray(change.allocations)) {
      change.allocations.forEach((alloc, allocIndex) => {});
    }
  });

  // Step 1: Calculate total shares currently issued (before new offering)
  let totalCurrentShares = 0;
  shareholders.forEach((shareholder) => {
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        totalCurrentShares += Number(share.allottedShares || 0);
      });
    }
  });

  // Step 2: Calculate total shares on offer from all transactions
  // IMPORTANT: For transfers, do NOT add sharesOnOffer to denominator
  let totalSharesOnOffer = 0;
  const transferReductionByShareholderId = {}; // Track shares being transferred OUT

  shareCapitalChanges.forEach((change) => {
    // Check if this is a transfer transaction
    const isTransferChange =
      Array.isArray(change.allocations) &&
      change.allocations.some(
        (a) =>
          String(a?.type).toLowerCase() === "transfer" ||
          a?.isTransferor === true
      );

    const sharesOnOffer = Number(change.sharesOnOffer || 0);

    // For transfers, do NOT add to totalSharesOnOffer (keeps denominator constant)
    if (!isTransferChange) {
      totalSharesOnOffer += sharesOnOffer;
    } else {
    }

    // Track transferor reductions
    if (Array.isArray(change.allocations)) {
      change.allocations.forEach((allocation) => {
        if (
          (String(allocation?.type).toLowerCase() === "transfer" ||
            allocation?.isTransferor === true) &&
          allocation?.meta?.amount
        ) {
          const shareholderId = allocation.officialId;
          const reduction = Number(allocation.meta.amount || 0);
          transferReductionByShareholderId[shareholderId] =
            (transferReductionByShareholderId[shareholderId] || 0) + reduction;
        }
      });
    }
  });

  // Step 3: Calculate total shares after offering (denominator for percentage)
  // If no shares on offer, use current total as denominator (original state)
  const totalSharesAfterOffering =
    totalSharesOnOffer > 0
      ? totalCurrentShares + totalSharesOnOffer
      : totalCurrentShares;

  // Step 4: Calculate each shareholder's percentage
  const shareholderPercentages = {};

  shareholders.forEach((shareholder) => {
    const shareholderId = shareholder.id || shareholder._id;

    // Get shareholder's current shares
    let currentShares = 0;
    if (Array.isArray(shareholder.sharesAllotted)) {
      shareholder.sharesAllotted.forEach((share) => {
        currentShares += Number(share.allottedShares || 0);
      });
    }

    // Apply transfer reductions (shares being transferred OUT)
    const transferReduction =
      transferReductionByShareholderId[shareholderId] || 0;
    const adjustedCurrentShares = Math.max(
      0,
      currentShares - transferReduction
    );

    // Get allocated shares from current transactions
    let newlyAllocatedShares = 0;

    shareCapitalChanges.forEach((change, changeIndex) => {
      if (Array.isArray(change.allocations)) {
        const allocation = change.allocations.find((alloc) => {
          const match = String(alloc.officialId) === String(shareholderId);

          return match;
        });

        if (allocation) {
          const allocated = Number(allocation.allocatedShares || 0);
          newlyAllocatedShares += allocated;
        } else {
        }
      } else {
      }
    });

    // Total shares this shareholder will have (use adjusted current shares for transfers)
    const totalSharesForShareholder =
      adjustedCurrentShares + newlyAllocatedShares;

    // Calculate percentage: (shareholder's total shares) / (total company shares + shares on offer) * 100%
    const percentage =
      totalSharesAfterOffering > 0
        ? roundNumber(
            (totalSharesForShareholder / totalSharesAfterOffering) * 100,
            2
          )
        : 0;

    shareholderPercentages[shareholderId] = {
      name: shareholder.name,
      totalPercentage: percentage,
      currentShares,
      newlyAllocatedShares,
      totalSharesForShareholder,
      totalSharesAfterOffering,
    };
  });

  return shareholderPercentages;
};

/**
 * Compare shareholding percentages and identify changes
 * @param {Object} originalPercentages - Original shareholding percentages
 * @param {Object} newPercentages - New shareholding percentages after allocation
 * @returns {Object} Object containing changes by shareholder ID
 */
export const compareShareholdingPercentages = (
  originalPercentages,
  newPercentages
) => {
  const changes = {};

  // Compare ALL shareholders present in either original or new
  const allShareholderIds = new Set([
    ...Object.keys(originalPercentages || {}),
    ...Object.keys(newPercentages || {}),
  ]);

  allShareholderIds.forEach((shareholderId) => {
    const original = originalPercentages[shareholderId];
    const updated = newPercentages[shareholderId];

    // 1. Economic Percentage
    const originalEconomic =
      typeof original?.economicPercentage === "number"
        ? original.economicPercentage
        : original?.totalPercentage || 0;
    const newEconomic =
      typeof updated?.economicPercentage === "number"
        ? updated.economicPercentage
        : updated?.totalPercentage || 0;

    // 2. Voting Percentage
    const originalVoting =
      typeof original?.votingPercentage === "number"
        ? original.votingPercentage
        : 0;
    const newVoting =
      typeof updated?.votingPercentage === "number"
        ? updated.votingPercentage
        : 0;

    // Detect changes (threshold 0.001)
    const economicDiff = Math.abs(originalEconomic - newEconomic);
    const votingDiff = Math.abs(originalVoting - newVoting);
    const hasEconomicChange = economicDiff > 0.001;
    const hasVotingChange = votingDiff > 0.001;

    if (hasEconomicChange || hasVotingChange) {
      changes[shareholderId] = {
        name: updated?.name || original?.name || "Unknown",

        originalPercentage: originalEconomic,
        newPercentage: newEconomic,
        shareholdingChange: roundNumber(newEconomic - originalEconomic, 2),

        originalVotingPercentage: originalVoting,
        newVotingPercentage: newVoting,
        votingRightsChange: roundNumber(newVoting - originalVoting, 2),

        change: roundNumber(newEconomic - originalEconomic, 2), // Legacy field
        hasIncreased: newEconomic > originalEconomic,
        hasDecreased: newEconomic < originalEconomic,
      };
    }
  });

  return changes;
};

/**
 * Calculate cumulative beneficial ownership (Direct + Indirect)
 * @param {Object} beneficialOwnershipForm - Beneficial ownership form data
 * @returns {Number} Total beneficial ownership percentage
 */
export const calculateCumulativeOwnership = (beneficialOwnershipForm) => {
  if (!beneficialOwnershipForm) return 0;

  const direct = Number(beneficialOwnershipForm.directPercentShareholding || 0);
  const indirect = Number(
    beneficialOwnershipForm.indirectPercentShareholding || 0
  );

  return roundNumber(direct + indirect, 2);
};

/**
 * Debug function to test percentage calculations with sample data
 */
export const debugPercentageCalculations = () => {
  const sampleShareholders = [
    {
      id: "1",
      name: "Person A",
      sharesAllotted: [{ classOfShares: "Ordinary", allottedShares: 50 }],
    },
    {
      id: "2",
      name: "Person B",
      sharesAllotted: [{ classOfShares: "Ordinary", allottedShares: 50 }],
    },
  ];

  const sampleNominalCapital = []; // Not used in new calculation

  // Before allotment (original state)
  const sampleOriginalChanges = [];

  // After allotment: 40 shares allocated to Person A from 60 shares on offer
  const sampleNewChanges = [
    {
      classOfShares: "Ordinary",
      sharesOnOffer: 60,
      allocations: [
        { officialId: "1", allocatedShares: 40 },
        { officialId: "2", allocatedShares: 0 }, // Person B gets no new shares
      ],
    },
  ];

  const originalPercentages = calculateShareholdingPercentages(
    sampleShareholders,
    sampleNominalCapital,
    sampleOriginalChanges
  );

  const newPercentages = calculateShareholdingPercentages(
    sampleShareholders,
    sampleNominalCapital,
    sampleNewChanges
  );

  const changes = compareShareholdingPercentages(
    originalPercentages,
    newPercentages
  );

  return { originalPercentages, newPercentages, changes };
};

/**
 * Get all shareholders from company officials
 * @param {Array} companyOfficials - Array of company officials
 * @param {string} classOfShares - Optional filter by class of shares
 * @returns {Array} Array of shareholder objects
 */
export const getShareholdersFromOfficials = (
  companyOfficials = [],
  classOfShares = null
) => {
  return companyOfficials
    .filter((official) => {
      const hasDesignation = [
        "Shareholder",
        "Joint Shareholder",
        "Director Shareholder",
        "Director",
      ].includes(official?.designation);

      const hasShares =
        Array.isArray(official?.sharesAllotted) &&
        official.sharesAllotted.length > 0 &&
        official.sharesAllotted.some((s) => Number(s.allottedShares || 0) > 0);

      return hasDesignation || hasShares;
    })
    .map((official) => {
      let name = official?.fullName;

      // Handle different name formats
      if (Array.isArray(name)) {
        name = name.join(", ");
      } else if (!name) {
        name =
          official?.names?.companyName ||
          `${official?.names?.firstName || ""} ${
            official?.names?.lastName || ""
          }`.trim() ||
          "Unknown";
      }

      let currentShares = 0;
      if (Array.isArray(official?.sharesAllotted)) {
        const relevantShares = classOfShares
          ? official.sharesAllotted.filter(
              (share) => share.classOfShares === classOfShares
            )
          : official.sharesAllotted;

        currentShares = relevantShares.reduce(
          (sum, share) => sum + Number(share.allottedShares || 0),
          0
        );
      }

      return {
        id: official._id,
        name,
        currentShares,
        sharesAllotted: official.sharesAllotted || [],
      };
    });
};

/**
 * Get aggregated shareholders from company officials (grouping Joint Shareholders)
 * @param {Array} companyOfficials - Array of company officials
 * @param {string} classOfShares - Optional filter by class of shares
 * @returns {Array} Array of shareholder objects (JS Groups aggregated)
 */
export const getAggregatedShareholdersFromOfficials = (
  companyOfficials = [],
  classOfShares = null
) => {
  // 1. Filter relevant officials
  const relevantOfficials = companyOfficials.filter((official) => {
    const hasDesignation = [
      "Shareholder",
      "Joint Shareholder",
      "Director Shareholder",
      "Director",
    ].includes(official?.designation);

    const hasShares =
      Array.isArray(official?.sharesAllotted) &&
      official.sharesAllotted.length > 0 &&
      official.sharesAllotted.some((s) => Number(s.allottedShares || 0) > 0);

    return hasDesignation || hasShares;
  });

  // 2. Group by JSGroupID
  const groups = {};
  const individuals = [];

  relevantOfficials.forEach((official) => {
    if (official.JSGroupID) {
      if (!groups[official.JSGroupID]) {
        groups[official.JSGroupID] = [];
      }
      groups[official.JSGroupID].push(official);
    } else {
      individuals.push(official);
    }
  });

  // 3. Process individuals
  const individualShareholders = individuals.map((official) => {
    let name = official?.fullName;
    if (Array.isArray(name)) {
      name = name.join(", ");
    } else if (!name) {
      name =
        official?.names?.companyName ||
        `${official?.names?.firstName || ""} ${
          official?.names?.lastName || ""
        }`.trim() ||
        "Unknown";
    }

    let currentShares = 0;
    let sharesAllotted = [];
    if (Array.isArray(official?.sharesAllotted)) {
      sharesAllotted = official.sharesAllotted;
      const relevantShares = classOfShares
        ? official.sharesAllotted.filter(
            (share) => share.classOfShares === classOfShares
          )
        : official.sharesAllotted;

      currentShares = relevantShares.reduce(
        (sum, share) => sum + Number(share.allottedShares || 0),
        0
      );
    }

    return {
      id: official._id,
      name,
      currentShares,
      sharesAllotted,
    };
  });

  // 4. Process Groups
  const groupShareholders = Object.keys(groups).map((groupId) => {
    const groupMembers = groups[groupId];
    // Sort by JSGroupOrder to find the "last" member for ID consistency
    const sortedMembers = _sortBy(groupMembers, ["JSGroupOrder"]);
    const lastMember = sortedMembers[sortedMembers.length - 1];

    // Aggregate Shares
    let currentShares = 0;
    let allSharesAllotted = [];

    groupMembers.forEach((member) => {
      if (Array.isArray(member.sharesAllotted)) {
        allSharesAllotted = [...allSharesAllotted, ...member.sharesAllotted];
        const relevantShares = classOfShares
          ? member.sharesAllotted.filter(
              (share) => share.classOfShares === classOfShares
            )
          : member.sharesAllotted;

        currentShares += relevantShares.reduce(
          (sum, share) => sum + Number(share.allottedShares || 0),
          0
        );
      }
    });

    return {
      id: lastMember._id, // Match the table row ID
      name: "Joint Shareholder Group", // Label
      currentShares,
      sharesAllotted: allSharesAllotted, // Pass all shares for breakdown
    };
  });

  return [...individualShareholders, ...groupShareholders];
};
